import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, FileText, CheckCircle, AlertTriangle, Clock, CreditCard,
  Receipt, Building, Users, CalendarDays, DollarSign, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_BADGES = {
  trial: { label: 'Trial', className: 'bg-sky-100 text-sky-700 border-sky-200', icon: Clock },
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  deactivated: { label: 'Deactivated', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: AlertTriangle },
  read_only: { label: 'Read Only', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  warning: { label: 'Payment Due', className: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
};

const INV_BADGES = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Unpaid', className: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const fmtMoney = (amount, currency) => {
  if (amount == null) return '—';
  const cur = currency || 'USD';
  return `${cur} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OfficeBilling = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const { getAuthHeaders } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchBilling = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/billing/my-billing`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load billing info');
    } catch { toast.error('Network error'); }
    finally { setLoading(false); }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  const downloadPdf = async (invId, invNumber) => {
    try {
      const res = await fetch(`${API}/api/billing/invoices/${invId}/pdf`, { headers: getAuthHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else toast.error('Failed to download PDF');
    } catch { toast.error('Network error'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="billing-loading">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-16 text-muted-foreground">
      <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p>No billing information available</p>
    </div>
  );

  const { config, invoices, active_users } = data;
  const sb = STATUS_BADGES[config.billing_status] || STATUS_BADGES.active;
  const StatusIcon = sb.icon;

  const unpaidInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'overdue');
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const totalPaid = paidInvoices.reduce((sum, i) => sum + (i.paid_amount || i.total_amount), 0);

  return (
    <div className="space-y-6" data-testid="office-billing-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Billing</h1>
        <p className="text-sm text-muted-foreground mt-1">View your subscription and invoices</p>
      </div>

      {/* Status banner */}
      {(config.billing_status === 'overdue' || config.billing_status === 'warning' || config.billing_status === 'deactivated' || config.billing_status === 'read_only') && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-start gap-3" data-testid="billing-warning">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 text-sm">
              {config.billing_status === 'deactivated' ? 'Account Deactivated' :
               config.billing_status === 'read_only' ? 'Account in Read-Only Mode' :
               'Payment Overdue'}
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              Please contact your administrator to resolve outstanding payments.
            </p>
          </div>
        </div>
      )}

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><StatusIcon className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Plan Status</p>
              <Badge variant="outline" className={`mt-1 ${sb.className}`}>{sb.label}</Badge>
              {config.is_trial && <p className="text-[10px] text-muted-foreground mt-1">Ends {fmtDate(config.trial_end_date)}</p>}
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Monthly Fee</p>
              <p className="font-bold text-lg">{fmtMoney(config.subscription_fee, config.currency)}</p>
              {config.per_user_fee > 0 && (
                <p className="text-[10px] text-muted-foreground">+ {fmtMoney(config.per_user_fee, config.currency)}/user above {config.free_user_threshold}</p>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Users className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Active Users</p>
              <p className="font-bold text-lg">{active_users}</p>
              <p className="text-[10px] text-muted-foreground">
                {active_users > config.free_user_threshold
                  ? `${active_users - config.free_user_threshold} billable above threshold`
                  : 'Within free threshold'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><CreditCard className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="font-bold text-lg">{fmtMoney(unpaidInvoices.reduce((s, i) => s + i.total_amount, 0), config.currency)}</p>
              <p className="text-[10px] text-muted-foreground">{unpaidInvoices.length} unpaid invoice(s)</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Invoices */}
      <div>
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Invoice History
        </h2>

        {invoices.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border rounded-lg">
            <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No invoices yet</p>
            {config.is_trial && <p className="text-xs mt-1">Invoices will appear after your trial period ends</p>}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Invoice #</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Period</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Amount</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Due Date</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Paid</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const ib = INV_BADGES[inv.status] || INV_BADGES.draft;
                  return (
                    <tr key={inv.id} className="border-t hover:bg-muted/30 transition-colors" data-testid={`invoice-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtMoney(inv.total_amount, inv.currency)}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={`text-[10px] ${ib.className}`}>{ib.label}</Badge></td>
                      <td className="px-4 py-3 text-xs">{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3 text-xs">{inv.paid_date ? fmtDate(inv.paid_date) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`download-${inv.id}`}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeBilling;
